import React, { useEffect, useRef, useState } from "react";

export function SubtitlePlayer({ subtitleFile, currentTime, audioRef }) {
  const containerRef = useRef(null);
  const [subtitles, setSubtitles] = useState([]);

  // Parse "00:00:01,521" into seconds
  const parseTimeToSeconds = (timeStr) => {
    const match = timeStr.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
    if (!match) return 0;
    const [, hh, mm, ss, ms] = match;
    return (
      parseInt(hh) * 3600 +
      parseInt(mm) * 60 +
      parseInt(ss) +
      parseInt(ms) / 1000
    );
  };

  // Parse SRT text
  const parseSRT = (srtText) => {
    const blocks = srtText.trim().split("\n\n");
    const subtitles = [];
    blocks.forEach((block) => {
      const lines = block.split("\n");
      if (lines.length >= 3) {
        const index = lines[0];
        const timeCode = lines[1];
        const text = lines.slice(2).join(" ");
        const timeMatch = timeCode.match(
          /(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/
        );
        if (timeMatch) {
          subtitles.push({
            index: parseInt(index),
            startTime: timeMatch[1],
            endTime: timeMatch[2],
            text: text,
          });
        }
      }
    });
    return subtitles;
  };

  // Fetch subtitle file
  useEffect(() => {
    if (!subtitleFile) return;
    fetch(subtitleFile)
      .then((res) => res.text())
      .then((text) => {
        setSubtitles(parseSRT(text));
      })
      .catch((err) => console.error("Error loading subtitles:", err));
  }, [subtitleFile]);

  // Handle click seek
  const handleSubtitleClick = (startTime) => {
    if (audioRef && audioRef.current) {
      if (typeof audioRef.current.seekTo === "function") {
        audioRef.current.seekTo(startTime);
      } else if (typeof audioRef.current.currentTime !== "undefined") {
        audioRef.current.currentTime = startTime;
      } else {
        console.warn("audioRef does not support seeking");
      }
    }
  };

  // Bind events and style spans
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const spans = container.querySelectorAll("[data-time]");
    spans.forEach((span) => {
      const timeStr = span.getAttribute("data-time");
      const seconds = parseTimeToSeconds(timeStr);

      // base style
      span.style.cursor = "pointer";
      span.style.borderRadius = "3px";
      span.style.transition = "background-color 0.2s ease";

      // click
      span.onclick = () => handleSubtitleClick(seconds);

      // hover
      span.onmouseenter = () => {
        if (!span.classList.contains("active")) {
          span.style.backgroundColor = "#d3d3d3";
        }
      };
      span.onmouseleave = () => {
        if (!span.classList.contains("active")) {
          span.style.backgroundColor = "transparent";
        }
      };
    });

    return () => {
      spans.forEach((span) => {
        span.onclick = null;
        span.onmouseenter = null;
        span.onmouseleave = null;
      });
    };
  }, [subtitles, audioRef]);

  // Highlight active span
  useEffect(() => {
    const container = containerRef.current;
    if (!container || currentTime == null) return;
    const spans = container.querySelectorAll("[data-time]");

    // reset first
    spans.forEach((s) => {
      s.classList.remove("active");
      s.style.backgroundColor = "transparent";
      s.style.fontWeight = "normal";
    });

    // find last span <= currentTime
    let activeSpan = null;
    spans.forEach((span) => {
      const timeStr = span.getAttribute("data-time");
      const seconds = parseTimeToSeconds(timeStr);
      if (currentTime >= seconds) {
        activeSpan = span;
      }
    });

    if (activeSpan) {
      activeSpan.classList.add("active");
      activeSpan.style.backgroundColor = "#ffeb3b";
    }
  }, [currentTime]);

  return (
    <div ref={containerRef} className="subtitle-container" style={{ lineHeight: "1.5" }}>
      {subtitles.map((subtitle) => (
        <span
          key={subtitle.index}
          data-time={subtitle.startTime}
          style={{  display: "inline", lineHeight:"1.5" }}
        >
          {subtitle.text}{" "}
        </span>
      ))}
    </div>
  );
}
